import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES } from "../data/services";
import { pageForService } from "../data/treatment-pages";
import { useBookingLinks } from "../lib/useBookingLinks";
import { trackSchedule } from "../lib/tracking";
import { BOOKING_URL, PHONE_DISPLAY, PHONE_TEL } from "../data/site";

// Vagaro booking widget (services + live availability), supplied by the owner.
const WIDGET_URL = "https://www.vagaro.com/Users/BusinessWidget.aspx?enc=MMLjhIwJMcwFQhXLL7ifVDnXRlTYXPIjR5wqEWlPcFpw27QbkYaPEk/sw/W5c4EsvUnCf6lQ5FKgg2Yyhfdo1UzQmoMtMr+Tpy9eK/F89w44MzPd+4uyKVHFFTzq1YwZSVbBE6cSeezDPwcoHTjaox4D37BXHgpXDhk0KzuJcHOxIQPZ6sAQKnaG9ioitQ82pZI+FNkXKz3Xko9UabiTl42sM5hJB0kqvoVnG2F4xWeX+4kGoocGgiy7Yzn1Zi+s8j0wKO0nqME/rqBjLfWxZ4TA00EVlsXfyw/asO3XgerHdzDHPM7deGosiUgrWPgZkiyYQpS9eCwObH9aPZj3BJnF4Cnp8Oyr4qjCiVWE3hmzJV91I2UQx8btRcWTjHY0qCW5u5SyEkwmaMSvCOjuFi7L3I+TNdQJN0ePklDfqrPtpe5VqnDAnKv6ZXuNuqWV";

const Picker = ({ active, onPick }) => (
  <div className="flex flex-wrap gap-2" role="tablist" data-testid="book-category-picker">
    {CATEGORIES.map((c) => (
      <button key={c.id} type="button" role="tab" aria-selected={active === c.id} onClick={() => onPick(c.id)} data-testid={`book-category-${c.id}`}
        className={`min-h-[44px] border px-5 text-[11px] uppercase tracking-[0.2em] transition-colors ${active === c.id ? "border-neutral-950 bg-neutral-950 text-white" : "border-neutral-300 bg-white text-neutral-800 hover:border-neutral-950"}`}>
        {c.name}
      </button>
    ))}
  </div>
);

const ServiceRow = ({ s, links }) => {
  const contact = pageForService(s.name)?.contactOnly;
  const cls = "inline-flex shrink-0 items-center gap-2 bg-neutral-950 px-5 py-3 text-[11px] font-medium uppercase tracking-[0.2em] text-white transition-colors hover:bg-neutral-700";
  return (
    <li className="flex flex-col gap-4 border-b border-neutral-200 py-5 sm:flex-row sm:items-center sm:justify-between" data-testid="book-service-row">
      <div>
        <p className="font-display text-xl text-neutral-950">{s.name}</p>
        <p className="mt-1 text-xs uppercase tracking-[0.2em] text-neutral-600">{s.price}</p>
      </div>
      {contact
        ? <Link to="/contact-us" className={cls} data-testid="book-service-contact">Contact Us to Schedule</Link>
        : <a href={links[s.name] || BOOKING_URL} target="_blank" rel="noreferrer" onClick={trackSchedule} className={cls} data-testid="book-service-btn">Book <ArrowUpRight size={14} strokeWidth={1.5} /></a>}
    </li>
  );
};

const Services = ({ id }) => {
  const links = useBookingLinks();
  const cat = CATEGORIES.find((c) => c.id === id);
  if (!cat) return <p className="mt-10 text-base font-light text-neutral-600" data-testid="book-category-prompt">Choose a category above to see its treatments and book.</p>;
  return (
    <div key={id} className="rise mt-10" data-testid="book-category-services">
      <p className="kicker">{cat.kicker}</p>
      <h2 className="font-display mt-2 text-3xl font-light text-neutral-950 md:text-4xl">{cat.name}</h2>
      <ul className="mt-6">{cat.services.map((s) => <ServiceRow key={s.name} s={s} links={links} />)}</ul>
    </div>
  );
};

export default function BookOnline() {
  const [cat, setCat] = useState("");
  return (
    <main data-testid="book-online-page">
      <section className="bg-neutral-950 px-6 py-16 text-white md:py-24">
        <div className="mx-auto max-w-[1100px]">
          <p className="rise text-[11px] uppercase tracking-[0.4em] text-neutral-400">Reserve Your Time</p>
          <h1 className="font-display rise d1 mt-4 text-4xl font-light leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="book-online-title">Book <em>online.</em></h1>
          <p className="rise d2 mt-6 max-w-lg text-base font-light leading-relaxed text-neutral-300">Pick a category, choose your treatment and book it in a few taps. Or browse our full live calendar below.</p>
        </div>
      </section>
      <section className="px-6 py-14 md:py-20">
        <div className="mx-auto max-w-[1100px]">
          <p className="kicker mb-5">Step 1 · Choose a category</p>
          <Picker active={cat} onPick={setCat} />
          <Services id={cat} />
        </div>
      </section>
      <section className="bg-[hsl(var(--stone))] px-2 py-10 md:px-8 md:py-16">
        <div className="mx-auto max-w-[1100px]">
          <p className="kicker mb-5 px-4 md:px-0">Or browse the full calendar</p>
          <div className="border border-neutral-950 bg-white" data-testid="book-online-frame">
            <iframe title="Book an appointment at Lux Veritae Skin Spa" src={WIDGET_URL} className="block h-[1400px] w-full border-0 md:h-[1200px]" data-testid="vagaro-widget" />
          </div>
        </div>
        <p className="mx-auto mt-8 max-w-[1100px] text-center text-sm font-light text-neutral-600">
          Calendar not loading? <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="underline" data-testid="book-online-fallback">Open booking in a new tab</a>, call or text <a href={`tel:${PHONE_TEL}`} className="underline" data-testid="book-online-call">{PHONE_DISPLAY}</a>, or <Link to="/contact" className="underline" data-testid="book-online-consult">request a consultation</Link>.
        </p>
      </section>
    </main>
  );
}
